import { useState, useEffect } from 'react';
import { useAuth } from "../../../../contexts/AuthContext";
import { db } from "../../../../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import getServerTimestamp from '../../../../functions/endpoints/GetServerTimestamp';
import { useNavigate, useParams } from "react-router";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import ErrorDisplay from '../../../../components/ErrorDisplay';
import Backdrop from "@mui/material/Backdrop";
import * as theme from '../../../../theme';
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import TruCard from '../../../../components/TruCard';
import AddableList from '../../../../components/AddableList';
import Grid from "@mui/material/Grid";

// Focus Scan Context
import { scanList, getScanLabel } from '../../../../contexts/FocusScanContext';

function deepMerge(target, source) {
  const output = { ...target };
  for (const key in source) {
    if (source.hasOwnProperty(key)) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key]) && target[key]) {
        output[key] = deepMerge(target[key], source[key]);
      } else {
        output[key] = source[key];
      }
    }
  }
  return output;
}

export default function EditClientReport() {
  const params = useParams();
  const clientId = params.clientId;
  const scanId = params.scanId;
  const reportId = params.reportId;
  const isOneOff = !!reportId;
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    nutritionalSupport: { bestSuperfood: '', dietSuggestion: '' },
    topPriorities: [],
    topSupplements: [],
    toxinBurden: {
      bacteria: 0,
      chemical: 0,
      emotional: 0,
      food: 0,
      fungal: 0,
      geopathic: 0,
      heavyMetal: 0,
      parasite: 0,
      mycoplasma: 0,
      mycotoxin: 0,
      viral: 0,
      vaccine: 0
    },
    emotions: {
      primaryDisharmony: '',
      factorsConnected: [],
      bachFlowerBalance: [],
      emotionalImbalances: [],
      emotionsAffecting: [],
      bodyStressors: [],
      wordsOfAffirmation: { words: [], description: '' },
      limitingBeliefs: [],
      soulFilters: ''
    },
    focusScans: [],
    supportiveTherapies: [],
    additionalSuggestions: [],
    yourSupplements: [],
    foodSensitivity: {
      beverages: [],
      dairy: [],
      fish: [],
      foodAdditives: [],
      fruits: [],
      grains: [],
      legumes: [],
      meatAlternatives: [],
      meat: [],
      nonDairyMilk: [],
      nutsSeeds: [],
      oilsFats: [],
      phenolics: [],
      spicesHerbs: [],
      sugarsSweeteners: [],
      vegetables: []
    },
    allergenSensitivity: {
      additionalIngredients: [],
      airPollutants: [],
      animalDander: [],
      chemicals: [],
      fossilFuels: [],
      metals: [],
      materials: [],
      pests: [],
      phenolics: [],
      pollens: [],
      plants: [],
      harmfulEnergies: []
    }
  });
  const [scan, setScan] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Collapsible sections state - first section (nutritionalSupport) starts expanded for immediate editing
  // All others start collapsed to reduce initial scrolling on this long form.
  const [expandedSections, setExpandedSections] = useState({
    nutritionalSupport: true,
    topPriorities: false,
    topSupplements: false,
    toxinBurden: false,
    emotions: false,
    focusScans: false,
    supportiveTherapies: false,
    additionalSuggestions: false,
    yourSupplements: false,
    foodSensitivity: false,
    allergenSensitivity: false,
  });

  const toggleSection = (sectionKey) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const clearError = () => {
    setError('');
  };

  // Fixed order for categories to ensure consistent UI ordering regardless of object key order from DB
  const foodSensitivityCategories = [
    'beverages',
    'dairy',
    'fish',
    'foodAdditives',
    'fruits',
    'grains',
    'legumes',
    'meatAlternatives',
    'meat',
    'nonDairyMilk',
    'nutsSeeds',
    'oilsFats',
    'phenolics',
    'spicesHerbs',
    'sugarsSweeteners',
    'vegetables'
  ];

  const allergenSensitivityCategories = [
    'additionalIngredients',
    'airPollutants',
    'animalDander',
    'chemicals',
    'fossilFuels',
    'metals',
    'materials',
    'pests',
    'phenolics',
    'pollens',
    'plants',
    'harmfulEnergies'
  ];

  // Formats camelCase keys into readable labels with spaces (e.g. harmfulEnergies → Harmful Energies)
  // Used for the "Add X" labels in Food Sensitivity and Allergen Sensitivity sections.
  const formatCategoryLabel = (key) => {
    // Insert space before uppercase letters (except the first character)
    const spaced = key.replace(/([a-z])([A-Z])/g, '$1 $2');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  };

  useEffect(() => {
    const getData = async () => {
      if (isOneOff && !reportId) {
        setError('Missing report ID for one-off edit.');
        return;
      }
      if (!isOneOff && (!clientId || !scanId)) {
        setError('Missing client or scan ID for standard edit.');
        return;
      }

      try {
        let docRef;
        if (isOneOff) {
          docRef = doc(db, "oneOffReports", reportId);
        } else {
          docRef = doc(db, "clients", clientId, "scans", scanId);
        }
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          const reportData = data.reportData || {};
          if (typeof reportData !== 'object' || reportData === null) {
            setError("This scan uses an older format and cannot be edited here.");
            navigate(isOneOff ? '/admin/reports' : `/clients/${clientId}/scans/${scanId}`);
            return;
          }

          // Ensure we always have complete category objects by merging with defaults
          const defaultFoodSensitivity = {
            beverages: [], dairy: [], fish: [], foodAdditives: [], fruits: [], grains: [],
            legumes: [], meatAlternatives: [], meat: [], nonDairyMilk: [], nutsSeeds: [],
            oilsFats: [], phenolics: [], spicesHerbs: [], sugarsSweeteners: [], vegetables: []
          };
          const defaultAllergenSensitivity = {
            additionalIngredients: [], airPollutants: [], animalDander: [], chemicals: [],
            fossilFuels: [], metals: [], materials: [], pests: [], phenolics: [], pollens: [],
            plants: [], harmfulEnergies: []
          };

          const foodSens = reportData.foodSensitivity || {};
          const allergenSens = reportData.allergenSensitivity || {};

          setFormData({
            nutritionalSupport: reportData.nutritionalSupport || { bestSuperfood: '', dietSuggestion: '' },
            topPriorities: reportData.topPriorities || [],
            topSupplements: reportData.topSupplements || [],
            toxinBurden: reportData.toxinBurden || {
              bacteria: 0, chemical: 0, emotional: 0, food: 0, fungal: 0, geopathic: 0,
              heavyMetal: 0, parasite: 0, mycoplasma: 0, mycotoxin: 0, viral: 0, vaccine: 0
            },
            emotions: {
              primaryDisharmony: reportData.emotions?.primaryDisharmony || '',
              factorsConnected: reportData.emotions?.factorsConnected || [],
              bachFlowerBalance: reportData.emotions?.bachFlowerBalance || [],
              emotionalImbalances: reportData.emotions?.emotionalImbalances || [],
              emotionsAffecting: reportData.emotions?.emotionsAffecting || [],
              bodyStressors: reportData.emotions?.bodyStressors || [],
              wordsOfAffirmation: reportData.emotions?.wordsOfAffirmation || { words: [], description: '' },
              limitingBeliefs: reportData.emotions?.limitingBeliefs || [],
              soulFilters: reportData.emotions?.soulFilters || ''
            },
            supportiveTherapies: reportData.supportiveTherapies || [],
            yourSupplements: reportData.yourSupplements || [],
            foodSensitivity: { ...defaultFoodSensitivity, ...foodSens },
            allergenSensitivity: { ...defaultAllergenSensitivity, ...allergenSens },
            focusScans: reportData.focusScans || [],
            additionalSuggestions: reportData.additionalSuggestions || []
          });
          setScan(data);
        } else {
          setError("Data not found.");
        }
      } catch (err) {
        setError("Failed to fetch: " + err.message);
      }
    };
    getData();
  }, [clientId, scanId, reportId, isOneOff, navigate]);

  const handleTextChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleNestedTextChange = (parent, child) => (e) => {
    setFormData({
      ...formData,
      [parent]: { ...formData[parent], [child]: e.target.value }
    });
  };

  const handleDoubleNestedTextChange = (parent, child, subchild) => (e) => {
    setFormData({
      ...formData,
      [parent]: {
        ...formData[parent],
        [child]: { ...formData[parent][child], [subchild]: e.target.value }
      }
    });
  };

  const handleToxinBurdenChange = (key) => (e) => {
    const value = parseInt(e.target.value, 10) || 0;
    setFormData({
      ...formData,
      toxinBurden: { ...formData.toxinBurden, [key]: value }
    });
  };

  const addItem = (field, value) => {
    setFormData({
      ...formData,
      [field]: [...formData[field], value.trim()]
    });
  };

  const removeItem = (field, index) => {
    const updated = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: updated });
  };

  const updateItem = (field, index, value) => {
    const updated = [...formData[field]];
    updated[index] = value;
    setFormData({ ...formData, [field]: updated });
  };

  const addToNestedArray = (parent, field, value) => {
    setFormData({
      ...formData,
      [parent]: {
        ...formData[parent],
        [field]: [...formData[parent][field], value.trim()]
      }
    });
  };

  const removeFromNestedArray = (parent, field, index) => {
    const updated = formData[parent][field].filter((_, i) => i !== index);
    setFormData({
      ...formData,
      [parent]: {
        ...formData[parent],
        [field]: updated
      }
    });
  };

  const updateNestedArrayItem = (parent, field, index, value) => {
    const updated = [...formData[parent][field]];
    updated[index] = value;
    setFormData({
      ...formData,
      [parent]: {
        ...formData[parent],
        [field]: updated
      }
    });
  };

  const addToDoubleNestedArray = (parent, child, field, value) => {
    setFormData({
      ...formData,
      [parent]: {
        ...formData[parent],
        [child]: {
          ...formData[parent][child],
          [field]: [...formData[parent][child][field], value.trim()]
        }
      }
    });
  };

  const removeFromDoubleNestedArray = (parent, child, field, index) => {
    const updated = formData[parent][child][field].filter((_, i) => i !== index);
    setFormData({
      ...formData,
      [parent]: {
        ...formData[parent],
        [child]: {
          ...formData[parent][child],
          [field]: updated
        }
      }
    });
  };

  const updateDoubleNestedArrayItem = (parent, child, field, index, value) => {
    const updated = [...formData[parent][child][field]];
    updated[index] = value;
    setFormData({
      ...formData,
      [parent]: {
        ...formData[parent],
        [child]: {
          ...formData[parent][child],
          [field]: updated
        }
      }
    });
  };

  const addToNestedObjectArray = (parent, field, defaultObj = { item: '', description: '' }) => {
    setFormData({
      ...formData,
      [parent]: {
        ...formData[parent],
        [field]: [...formData[parent][field], defaultObj]
      }
    });
  };

  const removeFromNestedObjectArray = (parent, field, index) => {
    const updated = formData[parent][field].filter((_, i) => i !== index);
    setFormData({
      ...formData,
      [parent]: {
        ...formData[parent],
        [field]: updated
      }
    });
  };

  const updateNestedObjectArray = (parent, field, index, key, value) => {
    const updatedArray = [...formData[parent][field]];
    updatedArray[index] = { ...updatedArray[index], [key]: value };
    setFormData({
      ...formData,
      [parent]: {
        ...formData[parent],
        [field]: updatedArray
      }
    });
  };

  // Focus Scans handlers
  const addFocusScan = (selectedValue) => {
    const selected = scanList.find(s => s.value === selectedValue);
    if (selected) {
      setFormData({
        ...formData,
        focusScans: [...formData.focusScans, { scan: selected.value, items: [], comment: '' }]
      });
    }
  };

  const removeFocusScan = (index) => {
    const updated = formData.focusScans.filter((_, i) => i !== index);
    setFormData({ ...formData, focusScans: updated });
  };

  const updateFocusScan = (index, field, value) => {
    const updated = [...formData.focusScans];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, focusScans: updated });
  };

  const updateFocusScanItem = (fIndex, iIndex, key, value) => {
    const updatedFocus = [...formData.focusScans];
    const updatedItems = [...updatedFocus[fIndex].items];
    updatedItems[iIndex] = { ...updatedItems[iIndex], [key]: value };
    updatedFocus[fIndex] = { ...updatedFocus[fIndex], items: updatedItems };
    setFormData({ ...formData, focusScans: updatedFocus });
  };

  const addItemDescriptionToFocusScan = (fIndex) => {
    const updatedFocus = [...formData.focusScans];
    const newItem = { item: '', description: '' };
    updatedFocus[fIndex].items = [...(updatedFocus[fIndex].items || []), newItem];
    setFormData({ ...formData, focusScans: updatedFocus });
  };

  const addItemListToFocusScan = (fIndex) => {
    const updatedFocus = [...formData.focusScans];
    const newItem = { item: '', items: [] };
    updatedFocus[fIndex].items = [...(updatedFocus[fIndex].items || []), newItem];
    setFormData({ ...formData, focusScans: updatedFocus });
  };

  const removeFocusScanItem = (fIndex, iIndex) => {
    const updatedFocus = [...formData.focusScans];
    const updatedItems = updatedFocus[fIndex].items.filter((_, i) => i !== iIndex);
    updatedFocus[fIndex] = { ...updatedFocus[fIndex], items: updatedItems };
    setFormData({ ...formData, focusScans: updatedFocus });
  };

  const addToFocusScanItemList = (fIndex, iIndex, value) => {
    const updatedFocus = [...formData.focusScans];
    const updatedItems = [...updatedFocus[fIndex].items];
    const currentList = updatedItems[iIndex].items || [];
    updatedItems[iIndex] = { ...updatedItems[iIndex], items: [...currentList, value.trim()] };
    updatedFocus[fIndex] = { ...updatedFocus[fIndex], items: updatedItems };
    setFormData({ ...formData, focusScans: updatedFocus });
  };

  const removeFromFocusScanItemList = (fIndex, iIndex, listIndex) => {
    const updatedFocus = [...formData.focusScans];
    const updatedItems = [...updatedFocus[fIndex].items];
    const currentList = updatedItems[iIndex].items || [];
    const newList = currentList.filter((_, idx) => idx !== listIndex);
    updatedItems[iIndex] = { ...updatedItems[iIndex], items: newList };
    updatedFocus[fIndex] = { ...updatedFocus[fIndex], items: updatedItems };
    setFormData({ ...formData, focusScans: updatedFocus });
  };

  const updateFocusScanItemListItem = (fIndex, iIndex, listIndex, value) => {
    const updatedFocus = [...formData.focusScans];
    const updatedItems = [...updatedFocus[fIndex].items];
    const currentList = [...(updatedItems[iIndex].items || [])];
    currentList[listIndex] = value;
    updatedItems[iIndex] = { ...updatedItems[iIndex], items: currentList };
    updatedFocus[fIndex] = { ...updatedFocus[fIndex], items: updatedItems };
    setFormData({ ...formData, focusScans: updatedFocus });
  };

  // Additional Suggestions handlers
  const addAdditionalSuggestion = () => {
    setFormData({
      ...formData,
      additionalSuggestions: [...formData.additionalSuggestions, { item: '', description: '' }]
    });
  };

  const removeAdditionalSuggestion = (index) => {
    const updated = formData.additionalSuggestions.filter((_, i) => i !== index);
    setFormData({ ...formData, additionalSuggestions: updated });
  };

  const updateAdditionalSuggestion = (index, key, value) => {
    const updatedArray = [...formData.additionalSuggestions];
    updatedArray[index] = { ...updatedArray[index], [key]: value };
    setFormData({ ...formData, additionalSuggestions: updatedArray });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const timestamp = await getServerTimestamp();
      const updatedReport = { ...formData };

      let docRef;
      if (isOneOff) {
        docRef = doc(db, "oneOffReports", reportId);
      } else {
        docRef = doc(db, "clients", clientId, "scans", scanId);
      }

      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new Error("Document does not exist");
      }

      const currentData = docSnap.data();
      const currentReportData = currentData.reportData || {};
      const mergedReportData = deepMerge(currentReportData, updatedReport);

      await updateDoc(docRef, {
        reportData: mergedReportData,
        dateModified: timestamp,
      });

      navigate(isOneOff ? `/admin/reports/session/${reportId}` : `/clients/${clientId}/scans/${scanId}`);
    } catch (err) {
      setError("Failed to update client report: " + err.message);
    }

    setLoading(false);
  };

  return (
    <div style={theme.Container.basicWide}>
      <div style={theme.Heading}>EDIT CLIENT REPORT</div>
      <div style={{ marginTop: 15, marginBottom: 30, position: 'relative' }}>
        <Box sx={{ marginBottom: 2 }}>
          <ErrorDisplay
            message={error}
            type={'error'}
            clearMessage={clearError}
          />
        </Box>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ ...theme.FullForm.form, gap: 4 }}
        >
          <TruCard 
            title={'Nutritional Support'}
            collapsible={true}
            expanded={expandedSections.nutritionalSupport}
            onToggle={() => toggleSection('nutritionalSupport')}
          >
            <TextField
              label="Best Superfood"
              fullWidth
              value={formData.nutritionalSupport.bestSuperfood}
              onChange={handleNestedTextChange('nutritionalSupport', 'bestSuperfood')}
              disabled={loading}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Diet Suggestion"
              fullWidth
              multiline
              rows={2}
              value={formData.nutritionalSupport.dietSuggestion}
              onChange={handleNestedTextChange('nutritionalSupport', 'dietSuggestion')}
              disabled={loading}
            />
          </TruCard>

          <TruCard 
            title={'Top Priorities'}
            collapsible={true}
            expanded={expandedSections.topPriorities}
            onToggle={() => toggleSection('topPriorities')}
          >
            <AddableList
              label="Add Priority"
              items={formData.topPriorities}
              onAdd={(value) => addItem('topPriorities', value)}
              onRemove={(index) => removeItem('topPriorities', index)}
              onUpdate={(index, value) => updateItem('topPriorities', index, value)}
              allowEdit={true}
              disabled={loading}
              isObject={false}
              multiline={true}
            />
          </TruCard>

          <TruCard 
            title={'Top Supplements'}
            collapsible={true}
            expanded={expandedSections.topSupplements}
            onToggle={() => toggleSection('topSupplements')}
          >
            <AddableList
              label="Add Supplement"
              items={formData.topSupplements}
              onAdd={(value) => addItem('topSupplements', value)}
              onRemove={(index) => removeItem('topSupplements', index)}
              onUpdate={(index, value) => updateItem('topSupplements', index, value)}
              allowEdit={true}
              disabled={loading}
              isObject={false}
              multiline={false}
            />
          </TruCard>

          <TruCard 
            title={'Toxin Burden'}
            collapsible={true}
            expanded={expandedSections.toxinBurden}
            onToggle={() => toggleSection('toxinBurden')}
          >
            <Grid container spacing={2}>
              {Object.keys(formData.toxinBurden).map((key) => (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <TextField
                    label={key.charAt(0).toUpperCase() + key.slice(1)}
                    type="number"
                    fullWidth
                    value={formData.toxinBurden[key]}
                    onChange={handleToxinBurdenChange(key)}
                    disabled={loading}
                  />
                </Grid>
              ))}
            </Grid>
          </TruCard>

          <TruCard 
            title={'Emotions'}
            collapsible={true}
            expanded={expandedSections.emotions}
            onToggle={() => toggleSection('emotions')}
          >
            <TextField
              label="Primary Disharmony"
              fullWidth
              value={formData.emotions.primaryDisharmony}
              onChange={handleNestedTextChange('emotions', 'primaryDisharmony')}
              disabled={loading}
              sx={{ mb: 2 }}
            />
            <AddableList
              label="Add Factor Connected"
              items={formData.emotions.factorsConnected}
              onAdd={(value) => addToNestedArray('emotions', 'factorsConnected', value)}
              onRemove={(index) => removeFromNestedArray('emotions', 'factorsConnected', index)}
              onUpdate={(index, value) => updateNestedArrayItem('emotions', 'factorsConnected', index, value)}
              allowEdit={true}
              disabled={loading}
              isObject={false}
              multiline={true}
              sx={{ mb: 2 }}
            />
            <Box sx={{ mb: 2 }}>
              {formData.emotions.bachFlowerBalance.map((balance, index) => (
                <Box key={index} sx={{ mb: 2, border: '1px solid #ccc', p: 2, borderRadius: 1, mb: 2 }}>
                  <div style={{ marginBottom: 16, fontWeight: 'bold' }}>Bach Flower</div>
                  <TextField
                    label={`Item ${index + 1}`}
                    fullWidth
                    value={balance.item}
                    onChange={(e) => updateNestedObjectArray('emotions', 'bachFlowerBalance', index, 'item', e.target.value)}
                    disabled={loading}
                    sx={{ mb: 2}}
                  />
                  <TextField
                    label={`Description ${index + 1}`}
                    fullWidth
                    multiline
                    rows={2}
                    value={balance.description}
                    onChange={(e) => updateNestedObjectArray('emotions', 'bachFlowerBalance', index, 'description', e.target.value)}
                    disabled={loading}
                    sx={{ mb: 2 }}
                  />
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => removeFromNestedObjectArray('emotions', 'bachFlowerBalance', index)}
                    disabled={loading}
                  >
                    Remove Bach Flower
                  </Button>
                </Box>
              ))}
              <Button
                variant="outlined"
                onClick={() => addToNestedObjectArray('emotions', 'bachFlowerBalance', { item: '', description: '' })}
                disabled={loading}
              >
                Add Bach Flower
              </Button>
            </Box>
            <Box sx={{ mb: 2 }}>
              <AddableList
                label="Add Emotional Imbalance"
                items={formData.emotions.emotionalImbalances}
                onAdd={(value) => addToNestedArray('emotions', 'emotionalImbalances', value)}
                onRemove={(index) => removeFromNestedArray('emotions', 'emotionalImbalances', index)}
                onUpdate={(index, value) => updateNestedArrayItem('emotions', 'emotionalImbalances', index, value)}
                allowEdit={true}
                disabled={loading}
                isObject={false}
                multiline={true}
                sx={{ mb: 2 }}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <AddableList
                label="Add Emotion Affecting"
                items={formData.emotions.emotionsAffecting}
                onAdd={(value) => addToNestedArray('emotions', 'emotionsAffecting', value)}
                onRemove={(index) => removeFromNestedArray('emotions', 'emotionsAffecting', index)}
                onUpdate={(index, value) => updateNestedArrayItem('emotions', 'emotionsAffecting', index, value)}
                allowEdit={true}
                disabled={loading}
                isObject={false}
                multiline={true}
                sx={{ mb: 2 }}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <AddableList
                label="Add Body Stressor"
                items={formData.emotions.bodyStressors}
                onAdd={(value) => addToNestedArray('emotions', 'bodyStressors', value)}
                onRemove={(index) => removeFromNestedArray('emotions', 'bodyStressors', index)}
                onUpdate={(index, value) => updateNestedArrayItem('emotions', 'bodyStressors', index, value)}
                allowEdit={true}
                disabled={loading}
                isObject={false}
                multiline={true}
                sx={{ mb: 2 }}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <AddableList
                label="Add Words of Affirmation"
                items={formData.emotions.wordsOfAffirmation.words}
                onAdd={(value) => addToDoubleNestedArray('emotions', 'wordsOfAffirmation', 'words', value)}
                onRemove={(index) => removeFromDoubleNestedArray('emotions', 'wordsOfAffirmation', 'words', index)}
                onUpdate={(index, value) => updateDoubleNestedArrayItem('emotions', 'wordsOfAffirmation', 'words', index, value)}
                allowEdit={true}
                disabled={loading}
                isObject={false}
                multiline={true}
              />
              <TextField
                label="Words of Affirmation Description"
                fullWidth
                multiline
                rows={2}
                value={formData.emotions.wordsOfAffirmation.description}
                onChange={handleDoubleNestedTextChange('emotions', 'wordsOfAffirmation', 'description')}
                disabled={loading}
                sx={{ mb: 2 }}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <AddableList
                label="Add Limiting Belief"
                items={formData.emotions.limitingBeliefs}
                onAdd={(value) => addToNestedArray('emotions', 'limitingBeliefs', value)}
                onRemove={(index) => removeFromNestedArray('emotions', 'limitingBeliefs', index)}
                onUpdate={(index, value) => updateNestedArrayItem('emotions', 'limitingBeliefs', index, value)}
                allowEdit={true}
                disabled={loading}
                isObject={false}
                multiline={true}
                sx={{ mb: 2 }}
              />
            </Box>
            <TextField
              label="Soul Filters"
              fullWidth
              multiline
              rows={2}
              value={formData.emotions.soulFilters}
              onChange={handleNestedTextChange('emotions', 'soulFilters')}
              disabled={loading}
            />
          </TruCard>

          <TruCard 
            title={'Focus Scans'}
            collapsible={true}
            expanded={expandedSections.focusScans}
            onToggle={() => toggleSection('focusScans')}
          >
            <Box sx={{ mb: 3 }}>
              <TextField
                select
                label="Add New Focus Scan"
                fullWidth
                value=""
                onChange={(e) => addFocusScan(e.target.value)}
                disabled={loading}
              >
                {scanList.map((scan) => (
                  <MenuItem key={scan.value} value={scan.value}>
                    {scan.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {formData.focusScans.map((focus, fIndex) => (
              <Box
                key={fIndex}
                sx={{
                  mb: 5,
                  border: '2px solid #ddd',
                  p: 3,
                  borderRadius: 2,
                  backgroundColor: '#f9f9f9'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6">
                    {getScanLabel(focus.scan) || focus.scan}
                  </Typography>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => removeFocusScan(fIndex)}
                    disabled={loading}
                  >
                    Remove Focus Scan
                  </Button>
                </Box>

                <Box sx={{ mb: 4 }}>
                  {focus.items && focus.items.map((itm, iIndex) => {
                    const isDescType = itm.description !== undefined;
                    return (
                      <Box
                        key={iIndex}
                        sx={{
                          mb: 4,
                          p: 3,
                          border: '1px solid #ccc',
                          borderRadius: 2,
                          backgroundColor: 'white'
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                          {isDescType ? 'Item Description' : 'Item List'}
                        </Typography>

                        <TextField
                          label="Item"
                          fullWidth
                          value={itm.item || ''}
                          onChange={(e) => updateFocusScanItem(fIndex, iIndex, 'item', e.target.value)}
                          disabled={loading}
                          sx={{ mb: 2 }}
                        />

                        {isDescType ? (
                          <TextField
                            label="Description (optional)"
                            fullWidth
                            multiline
                            rows={3}
                            value={itm.description || ''}
                            onChange={(e) => updateFocusScanItem(fIndex, iIndex, 'description', e.target.value)}
                            disabled={loading}
                            placeholder="Enter description here..."
                          />
                        ) : (
                          <AddableList
                            label="Add Sub-Item"
                            items={itm.items || []}
                            onAdd={(value) => addToFocusScanItemList(fIndex, iIndex, value)}
                            onRemove={(listIndex) => removeFromFocusScanItemList(fIndex, iIndex, listIndex)}
                            onUpdate={(listIndex, value) => updateFocusScanItemListItem(fIndex, iIndex, listIndex, value)}
                            allowEdit={true}
                            disabled={loading}
                            isObject={false}
                            multiline={false}
                          />
                        )}

                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={() => removeFocusScanItem(fIndex, iIndex)}
                          disabled={loading}
                          sx={{ mt: 2 }}
                        >
                          Remove This Item
                        </Button>
                      </Box>
                    );
                  })}

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      onClick={() => addItemDescriptionToFocusScan(fIndex)}
                      disabled={loading}
                    >
                      Add Item Description
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => addItemListToFocusScan(fIndex)}
                      disabled={loading}
                    >
                      Add Item List
                    </Button>
                  </Box>
                </Box>

                <TextField
                  label="Comment / Notes"
                  fullWidth
                  multiline
                  rows={3}
                  value={focus.comment || ''}
                  onChange={(e) => updateFocusScan(fIndex, 'comment', e.target.value)}
                  disabled={loading}
                />
              </Box>
            ))}
          </TruCard>

          <TruCard 
            title={'Supportive Therapies'}
            collapsible={true}
            expanded={expandedSections.supportiveTherapies}
            onToggle={() => toggleSection('supportiveTherapies')}
          >
            <AddableList
              label="Add Therapy"
              items={formData.supportiveTherapies}
              onAdd={(value) => addItem('supportiveTherapies', value)}
              onRemove={(index) => removeItem('supportiveTherapies', index)}
              onUpdate={(index, value) => updateItem('supportiveTherapies', index, value)}
              allowEdit={true}
              disabled={loading}
              isObject={false}
              multiline={true}
            />
          </TruCard>

          <TruCard 
            title={'Additional Suggestions'}
            collapsible={true}
            expanded={expandedSections.additionalSuggestions}
            onToggle={() => toggleSection('additionalSuggestions')}
          >
            <Box sx={{ mb: 2 }}>
              {formData.additionalSuggestions.map((sugg, index) => (
                <Box key={index} sx={{ mb: 2, border: '1px solid #ccc', p: 2, borderRadius: 1 }}>
                  <TextField
                    label={`Item ${index + 1}`}
                    fullWidth
                    value={sugg.item}
                    onChange={(e) => updateAdditionalSuggestion(index, 'item', e.target.value)}
                    disabled={loading}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    label={`Description ${index + 1}`}
                    fullWidth
                    multiline
                    rows={2}
                    value={sugg.description}
                    onChange={(e) => updateAdditionalSuggestion(index, 'description', e.target.value)}
                    disabled={loading}
                    sx={{ mb: 2 }}
                  />
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => removeAdditionalSuggestion(index)}
                    disabled={loading}
                  >
                    Remove Suggestion
                  </Button>
                </Box>
              ))}
              <Button
                variant="outlined"
                onClick={addAdditionalSuggestion}
                disabled={loading}
              >
                Add Suggestion
              </Button>
            </Box>
          </TruCard>

          <TruCard 
            title={'Your Supplements'}
            collapsible={true}
            expanded={expandedSections.yourSupplements}
            onToggle={() => toggleSection('yourSupplements')}
          >
            <AddableList
              label="Add Supplement"
              items={formData.yourSupplements}
              onAdd={(value) => addItem('yourSupplements', value)}
              onRemove={(index) => removeItem('yourSupplements', index)}
              onUpdate={(index, value) => updateItem('yourSupplements', index, value)}
              allowEdit={true}
              disabled={loading}
              isObject={false}
              multiline={false}
            />
          </TruCard>

          <TruCard 
            title={'Food Sensitivity'}
            collapsible={true}
            expanded={expandedSections.foodSensitivity}
            onToggle={() => toggleSection('foodSensitivity')}
          >
            {foodSensitivityCategories.map((category) => (
              <Box key={category} sx={{ mb: 3 }}>
                <AddableList
                  label={`Add ${formatCategoryLabel(category)}`}
                  items={formData.foodSensitivity[category] || []}
                  onAdd={(value) => addToNestedArray('foodSensitivity', category, value)}
                  onRemove={(index) => removeFromNestedArray('foodSensitivity', category, index)}
                  onUpdate={(index, value) => updateNestedArrayItem('foodSensitivity', category, index, value)}
                  allowEdit={true}
                  disabled={loading}
                  isObject={false}
                  multiline={false}
                />
              </Box>
            ))}
          </TruCard>

          <TruCard 
            title={'Allergen Sensitivity'}
            collapsible={true}
            expanded={expandedSections.allergenSensitivity}
            onToggle={() => toggleSection('allergenSensitivity')}
          >
            {allergenSensitivityCategories.map((category) => (
              <Box key={category} sx={{ mb: 3 }}>
                <AddableList
                  label={`Add ${formatCategoryLabel(category)}`}
                  items={formData.allergenSensitivity[category] || []}
                  onAdd={(value) => addToNestedArray('allergenSensitivity', category, value)}
                  onRemove={(index) => removeFromNestedArray('allergenSensitivity', category, index)}
                  onUpdate={(index, value) => updateNestedArrayItem('allergenSensitivity', category, index, value)}
                  allowEdit={true}
                  disabled={loading}
                  isObject={false}
                  multiline={false}
                />
              </Box>
            ))}
          </TruCard>

          <Box sx={{ display: "flex", width: '100%', gap: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              sx={{ flex: 1 }}
              onClick={() => navigate(isOneOff ? `/admin/reports/session/${reportId}` : `/clients/${clientId}/scans/${scanId}`)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ flex: 3 }}
            >
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </Box>
        </Box>
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, position: 'absolute', borderRadius: 1 }}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>
    </div>
  );
}